import { AccionModal, EstructuraEspecifica, FiltroTableMasterGeneral, CondicionControl } from '@app/shared';
import { Component, Input, Output, EventEmitter, OnInit, QueryList, ViewChildren, OnDestroy } from '@angular/core';
import { FormGroup, AbstractControl, Validators, ValidatorFn, FormControl } from '@angular/forms';
import { BaseControl } from './shared/controls/base-control';
import { Container } from './shared/controls/container';
import { FormControlService } from './shared/form-control.service';
import { DynamicFormControlComponent } from './dynamic-form-control/dynamic-form-control.component';
import { environment } from '@env/environment';
import { Observable } from 'rxjs/Observable';
import { isEmpty, find, indexOf } from 'lodash';
import { Static } from '../util/static';
import { Subject } from 'rxjs';
import { DynamicFormControlDataService } from './shared/dynamic-form-control.data.service';

@Component({
  selector: 'iw-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  entryComponents: [DynamicFormControlComponent],
  providers: [FormControlService,
    DynamicFormControlDataService
  ]
})
export class DynamicFormComponent implements OnInit, OnDestroy {
  // Decide si el formulario debe mostrar si esta realizando una petición.
  @Input() loading = false;
  // Establece si se muestra el btn para enviar el formulario.
  @Input() showBtnSubmit = true;
  // Establece si se inactiva el btn para enviar el formulario.
  @Input() inactiveBtnSubmit = false;
  // Establece clases css al boton de enviar.
  @Input() classBtnSubmit: string | Array<any>;
  // Establece el valor del boton de enviar.
  @Input() valueBtnSubmit = '';
  // Inactiva todo el formulario.
  @Input() disabledForm = false;
  // Inactiva todo el formulario.
  @Input() previewFlow = false;
  // Para mostrar información de debug.
  @Input() debug: boolean = environment.showDebugHelpers;
  // Establece si se fuerza realizar un reset del componente
  @Input() hardReset = false;
  // Desactiva la limpieza de los campos al precionar el boton de enviar
  @Input() disableReset = false;
  // Define cuando se debe retornar el formulario despues de haber sido creado
  @Input() returnCreatedForm = false;
  // Controla cuando es modificación de formulario
  @Input() modification = false;
  // Controla cuando se seleccionó agregar en la tabla y no modificar o ver
  @Input() addInTableModification: boolean = false;
  // Emite los datos introducidos en el formulario.
  @Output() sended: EventEmitter<any> = new EventEmitter();
  // Salida del formulario creado
  @Output() formCreated: EventEmitter<FormGroup> = new EventEmitter();
  // Emite el evento de accion de los modalSelect
  @Output() actionSelector: EventEmitter<AccionModal> = new EventEmitter();
  /**
   * Emite los cambios de los controles del formulario.
   *
   * @type {EventEmitter<any>}
   * @memberof DynamicFormComponent
   */
  @Output() controlChanged: EventEmitter<any> = new EventEmitter();

  // Referencia del formulario
  form: FormGroup;
  // Errores de validación
  formErrors = {};
  // Mensajes de validación de todos los campos
  validationMessages = {};
  // Contiene los controoles que se han modificado
  controlDirty: string[] = [];
  // Mensajes de validación por defecto
  validationMessagesDefault = {
    format: 'Formato de campo invalido.',
    required: 'Este campo es necesario.',
    minlength: 'El campo es muy corto.',
    maxlength: 'El campo es muy largo.',
    email: 'Email invalido.',
    number: 'Este campo debe ser numérico.',
    pattern: 'Caracteres incorrectos.'
  };
  // Lista de componentes de tipo DynamicFormControlComponent
  @ViewChildren(DynamicFormControlComponent) controlComponents: QueryList<DynamicFormControlComponent>;
  // Controla la visibilidad del formulario
  showForm = false;
  // Define cuando se esta modificando el formulario por las validaciones y no por cambios del usuario
  updateCondictions = false;
  // Lista de campos para el formulario
  private _controls: BaseControl<any>[] = [];
  private controlsInitial: BaseControl<any>[] = [];
  // Contiene tiempo de cambios a emitir del formulario
  private t: any;
  @Input()
  set controls(value: BaseControl<any>[]) {
    this._controls = value;
    this.controlsInitial = [];
    if (value && value.length) {
      value.forEach(control => {
        const newControl: Partial<BaseControl<any>> = {};
        Object.keys(control).forEach(key => {
          newControl[key] = control[key];
        });
        this.controlsInitial.push(newControl as BaseControl<any>);
      });
    }
    if (!isEmpty(value)) {
      this.showForm = true;
      this.init();
    } else {
      this.showForm = false;
    }
  }
  get controls() {
    return this._controls;
  }
  // Destruye y gestiona el memory leak
  private destroy: Subject<void> = new Subject<void>();

  constructor(private fcs: FormControlService,
    private dfds: DynamicFormControlDataService
    ) {}

  ngOnInit() {
    if(this.previewFlow){
      this.showBtnSubmit = false;
      this.disabledForm = true;
    }
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Solución para perdida de focus al cambiar el control.
   * Más info: https://stackoverflow.com/questions/42322968/
   *
   * @param {*} index
   * @param {*} item
   * @returns
   * @memberof DynamicFormComponent
   */
  trackByFn(index: any, item: any) {
    return index;
  }

  /**
   * Sobreescribe los mensajes de validación
   * definidos por default con los personalizados definidos en cada campo.
   *
   * @memberOf DynamicFormComponent
   */
  mergeValidationMessages(items?: BaseControl<any>[]) {
    const mergeJSON = require('locutus/php/array/array_merge');
    const controls = items ? items : this.controls;
    controls.forEach(el => {
      if (el.controlType === 'container') {
        this.mergeValidationMessages((<Container>el).controls);
      } else {
        if (!isEmpty(el.key)) {
          this.validationMessages[el.key] = mergeJSON(this.validationMessagesDefault, el.validations.messages);
        }
      }
    });
  }

  /**
   * Agrega un nuevo control al formulario formulario generado por Angular
   * Si existe el control, se elimina y se agrega de nuevo.
   *
   * @param {string} name
   * @param {AbstractControl} control
   * @memberof DynamicFormComponent
   */
  addFormControl(name: string, control: AbstractControl) {
    if (this.form.controls.hasOwnProperty(name)) {
      this.form.removeControl(name);
    }
    this.form.addControl(name, control);
  }

  /**
   * Actualiza los datos de un control del formulario generado por Angular.
   *
   * @param {string} element
   * @param {*} value
   * @memberof DynamicFormComponent
   */
  updateFormControl(element: string, value: any) {
    this.form.controls[element].setValue(value);
  }

  /**
   * Consulta el control solicitado en el DOM una vez este
   * renderizado en la plantilla(vista).
   *
   * @param {string} key
   * @returns {Promise<BaseControl<any>>}
   * @memberof DynamicFormComponent
   */
  getControl(key: string): Observable<BaseControl<any>> {
    return new Observable(obs => {
      const el = this.controlComponents.find(item => {
        return item.control.key === key;
      });
      if (el) {
        obs.next(el.control);
      }
    });
  }

  /**
   * Captura la información del formulario,
   * y la emite al exterior del componente.
   *
   * @param {boolean} [force=false]
   * @memberof DynamicFormComponent
   */
  onSubmit(force: boolean = false) {
    if (this.form.valid || force) {
      const form = {};
      if (Object.keys(this.form.value).length) {
        Object.keys(this.form.value).forEach(key => {
          const control = this.getConfigControl(key);
          if (control) {
            if (control.controlType === 'textarea' || control.controlType === 'textbox') {
              if (control['transform']) {
                if (this.form.value[key]) {
                  const value = String(this.form.value[key]);
                  if (control['transform'] === 'lowercase') {
                    form[key] = value.toLowerCase();
                  } else if (control['transform'] === 'uppercase') {
                    form[key] = value.toUpperCase();
                  } else {
                    form[key] = value;
                  }
                } else {
                  form[key] = null;
                }
              } else if (control['type'] === 'number') {
                form[key] = Number(Static.numberFormatWithoutThousandsSeparator(
                  this.form.value[key],
                  control['thousandsSeparator'],
                  control['decimalSeparator']
                ));
              } else {
                form[key] = this.form.value[key];
              }
            } else {
              form[key] = this.form.value[key];
            }
          } else {
            form[key] = this.form.value[key];
          }
        });
      }
      this.sended.emit(form);
      if (!this.disableReset) {
        this.reset();
      }
    }
  }

  /**
   * Resetea los controles a su estado inicial.
   *
   * @memberof DynamicFormComponent
   */
  reset() {
    this.form.reset();
    if (this.hardReset) {
      if (this.controlsInitial && this.controlsInitial.length) {
        this._controls = [];
        this.controlsInitial.forEach(control => {
          const newControl: Partial<BaseControl<any>> = {};
          Object.keys(control).forEach(key => {
            newControl[key] = control[key];
          });
          this._controls.push(newControl as BaseControl<any>);
        });
      }
      this.form.reset();
      this.init();
    }
  }

  /**
   * Crea y inicializa el formulario.
   *
   * @private
   *
   * @memberOf DynamicFormComponent
   */
  private init() {
    // declara valor para controlar el inicio del componente (condiciones)
    const startForm = true;
    // Se setea la propiedad disable en true si la propiedad del formulario disabledForm esta en true
    this.controls.map(el => {
        if (this.disabledForm) {
          if (el.controlType === 'container') {
            el['controls'] = this.formatDisabledForm(el['controls']);
          } else {
            if (!el.skipDisabled) {
              el.disabled = true;
            }
          }
        }
      // }
    });
    // Crear el formulario con los controles generados en el FormControlService
    this.form = this.fcs.createFormGroup(this.controls);
    // Se actualiza el formulario segun sus validaciones
    this.updateFormGroup(this.controls, this.form.value, startForm);
    // Definir mensajes de validación
    this.mergeValidationMessages();
    // Evaluar los cambios producidos en los controles
    // this.form.valueChanges.subscribe(data => {
    //   if (!this.updateCondictions) {
    //     // Se actualiza el formulario segun sus validaciones
    //     this.updateFormGroup(this.controls, data);
    //     // Validar los campos
    //     this.validateControls();
    //     return;
    //   }
    // });
    // Controla campo por campo los cambios realizados en el formulario
    this.fcs.getAllControls(this.controls).forEach(control => {
      if (this.form.controls[control.key]) {
        if (
          control['type'] === 'checkbox' ||
          control.controlType === 'custom' ||
          control.controlType === 'datetimepicker' ||
          control.controlType === 'dropdown' ||
          control.controlType === 'modalSelect' ||
          control.controlType === 'radio'
        ) {
          this.form.controls[control.key].valueChanges.subscribe(val => {
            this.eventChange(control, false, val);
            if (indexOf(this.controlDirty, control.key) === -1) {
              this.controlDirty.push(String(control.key));
            }
          });
        }
      }
    });
    // (re)set validación de errores
    this.configureMessagesValidation();

    if (this.returnCreatedForm) {
      this.formCreated.emit(this.form);
    }
  }

  /**
   * Controla que el cambio se emita una vez el usuario deje de escribir en el campo
   *
   * @param {BaseControl<any>} control
   * @memberof DynamicFormComponent
   */
  dataChangeInput(control: BaseControl<any>) {
    if (!this.inactiveBtnSubmit) {
      this.inactiveBtnSubmit = true;
    }
    if (this.t) {
      clearTimeout(this.t);
    }
    this.t = setTimeout(() => {
      this.eventChange(control, true);
      this.inactiveBtnSubmit = false;
      if (indexOf(this.controlDirty, control.key) === -1) {
        this.controlDirty.push(String(control.key));
      }
    }, 800);
  }

  /**
   * Emite el cambio en el formulario
   *
   * @private
   * @param {BaseControl<any>} control
   * @param {boolean} directFormValue
   * @param {*} [val]
   * @memberof DynamicFormComponent
   */
  private eventChange(control: BaseControl<any>, directFormValue: boolean, val?: any ) {
    // Evaluar los cambios producidos en los controles
    if (!this.updateCondictions) {
      let value = val;
      if (directFormValue) {
        value = this.form.value[control.key];
      }
      // Actualizar data
      const data = this.form.value;
      data[control.key] = value;
      // Se actualiza el formulario segun sus validaciones
      this.updateFormGroup(this.controls, data);
      // Validar los campos
      this.validateControls();
      // Emitir cambios
      this.controlChanged.emit({
        change: data,
        form: this.form,
        controlChanged: control.key
      });
    }
  }

  /**
   * Prepara los controles del formulario para realizar las respectivas validaciones
   *
   * @private
   * @param {BaseControl<any>[]} elements
   * @memberof DynamicFormComponent
   */
  private updateFormGroup(elements: BaseControl<any>[], data: any, starForm: boolean = false) {
    this.updateCondictions = true;
    const notInclude = ['legend'];
    const allElements: BaseControl<any>[] = [];
    elements.forEach(el => {
      if (el.controlType === 'container') {
        const allSubElements: BaseControl<any>[] = this.fcs.getAllControls(el['controls']);
        allSubElements.forEach(element => {
          if (data[element.key] !== undefined) {
            element.value = data[element.key];
          }
          allElements.push(element);
        });
      } else {
        if (!notInclude.includes(el.controlType)) {
          if (data[el.key] !== undefined) {
            el.value = data[el.key];
          }
          if (el.controlType) {}
          allElements.push(el);
        }
      }
    });
    this.validationsForm(allElements, data, starForm);
  }

  /**
   * Realiza las validaciones configuradas para cada control modificando el formulario
   *
   * @private
   * @param {BaseControl<any>[]} allElements
   * @param {*} data
   * @memberof DynamicFormComponent
   */
  private validationsForm (allElements: BaseControl<any>[], data: any, starForm: boolean = false) {
    allElements.forEach((el) => {
      if (el.condictions && el.condictions.length) {
        let valBlock = true;
        let valUnlock = true;
        let valView = true;
        let valHide = true;
        let assignValue = true;
        let valReading = true;
        let valWriting = true;
        let firstBlock = true;
        let firstUnlock = true;
        let firstView = true;
        let firstHide = true;
        let firstReading = true;
        let firstWriting = true;
        let firstAssignValue = true;
        let conditonValid = false;

        let firstAssignMasterValue = true;
        let assignMasterValue = true;
        let firtTimeCondition = true;
        let valExpresion = false;

        let operactorNext = 'O';
        let specificEstructure: EstructuraEspecifica;
        let masterFilters: FiltroTableMasterGeneral[] = [];
        el.condictions.forEach(async (condiction, index) => {
          const comply = this.executeCondiction(allElements, condiction);
          switch (condiction.accion) {
            case 'Desbloquear':
              if (!firstUnlock) {
                if (!operactorNext || operactorNext === 'O') {
                  if (valUnlock || comply) {
                    valUnlock = true;
                  } else {
                    valUnlock = false;
                  }
                } else {
                  if (valUnlock && comply) {
                    valUnlock = true;
                  } else {
                    valUnlock = false;
                  }
                }
                operactorNext = condiction.operadorCondicion;
              } else {
                firstUnlock = false;
                firstBlock = true;
                firstView = true;
                firstHide = true;
                firstReading = true;
                firstWriting = true;
                firstAssignValue = true;
                valUnlock = comply;
                operactorNext = condiction.operadorCondicion;
              }
                if (!this.disabledForm && el.controlType !== 'label') {
                  if (valUnlock) {
                    this.form.controls[el.key].enable();
                  } else {
                    this.form.controls[el.key].disable();
                    if (index > 0 || el.condictions.length === 1) {
                      if (el['default']) {
                        this.form.controls[el.key].setValue(el['default']);
                      } else {
                        this.form.controls[el.key].reset();
                      }
                    }
                  }
                }
              
              break;
            case 'Mostrar':
              if (!firstView) {
                if (!operactorNext || operactorNext === 'O') {
                  if (valView || comply) {
                    valView = true;
                  } else {
                    valView = false;
                  }
                } else {
                  if (valView && comply) {
                    valView = true;
                  } else {
                    valView = false;
                  }
                }
                operactorNext = condiction.operadorCondicion;
              } else {
                firstView = false;
                firstUnlock = true;
                firstBlock = true;
                firstHide = true;
                firstReading = true;
                firstWriting = true;
                firstAssignValue = true;
                valView = comply;
                operactorNext = condiction.operadorCondicion;
              }
              if (valView) {
                if (el.controlType !== 'label') {
                  if(valExpresion==false){
                    this.form.controls[el.key].setValidators(el.validations.validators);
                  }
                  if (el.value) {
                    this.form.controls[el.key].setValue(el.value);
                  } else if (el['default']) {
                    this.form.controls[el.key].setValue(el['default']);
                  }
                }
                el.hiddenCondiction = false;
              } else {
                el.hiddenCondiction = true;
                if (el.controlType !== 'label') {
                  this.form.controls[el.key].clearValidators();
                  if (index > 0 || el.condictions.length === 1) {
                    if (el['default']) {
                      this.form.controls[el.key].setValue(el['default']);
                    } else {
                      this.form.controls[el.key].reset();
                    }
                  }
                }
              }
              break;
            case 'Lectura':
              if (!firstReading) {
                if (!operactorNext || operactorNext === 'O') {
                  if (valReading || comply) {
                    valReading = true;
                  } else {
                    valReading = false;
                  }
                } else {
                  if (valReading && comply) {
                    valReading = true;
                  } else {
                    valReading = false;
                  }
                }
                operactorNext = condiction.operadorCondicion;
              } else {
                firstReading = false;
                firstUnlock = true;
                firstBlock = true;
                firstView = true;
                firstHide = true;
                firstWriting = true;
                firstAssignValue = true;
                valReading = comply;
                operactorNext = condiction.operadorCondicion;
              }
              if (el.controlType !== 'label') {
                if (valReading) {
                  el.readonly = true;
                } else {
                  el.readonly = false;
                }
              }
              break;
            case 'Escritura':
              if (!firstWriting) {
                if (!operactorNext || operactorNext === 'O') {
                  if (valWriting || comply) {
                    valWriting = true;
                  } else {
                    valWriting = false;
                  }
                } else {
                  if (valWriting && comply) {
                    valWriting = true;
                  } else {
                    valWriting = false;
                  }
                }
                operactorNext = condiction.operadorCondicion;
              } else {
                firstWriting = false;
                firstUnlock = true;
                firstBlock = true;
                firstView = true;
                firstHide = true;
                firstReading = true;
                firstAssignValue = true;
                valReading = comply;
                operactorNext = condiction.operadorCondicion;
              }
              if (el.controlType !== 'label') {
                if (valWriting) {
                  el.readonly = false;
                } else {
                  el.readonly = true;
                }
              }
              break;
            case 'Ocultar':
              if (!firstHide) {
                if (!operactorNext || operactorNext === 'O') {
                  if (valHide || comply) {
                    valHide = true;
                  } else {
                    valHide = false;
                  }
                } else {
                  if (valHide && comply) {
                    valHide = true;
                  } else {
                    valHide = false;
                  }
                }
                operactorNext = condiction.operadorCondicion;
              } else {
                firstHide = false;
                firstUnlock = true;
                firstBlock = true;
                firstView = true;
                firstReading = true;
                firstWriting = true;
                firstAssignValue = true;
                valHide = comply;
                operactorNext = condiction.operadorCondicion;
              }
              if (valHide) {
                el.hiddenCondiction = true;
                if (el.controlType !== 'label') {
                  this.form.controls[el.key].clearValidators();
                  if (index > 0 || el.condictions.length === 1) {
                    if (el['default']) {
                      this.form.controls[el.key].setValue(el['default']);
                    } else {
                      this.form.controls[el.key].reset();
                    }
                  }
                }
              } else {
                if (el.controlType !== 'label') {
                  if(valExpresion==false){
                    this.form.controls[el.key].setValidators(el.validations.validators);
                  }
                  if (el.value) {
                    this.form.controls[el.key].setValue(el.value);
                  } else if (el['default']) {
                    this.form.controls[el.key].setValue(el['default']);
                  }
                }
                el.hiddenCondiction = false;
              }
              break;
            case 'Asignar valor':
              if (!firstAssignValue) {
                if (!assignValue) {
                  assignValue = comply;
                } else {
                  break;
                }
              } else {
                firstAssignValue = false;
                firstHide = true;
                firstUnlock = true;
                firstBlock = true;
                firstView = true;
                firstReading = true;
                firstWriting = true;
                assignValue = comply;
              }
              if (assignValue) {
                if (el.controlType !== 'label') {
                  if (condiction.fechaActualAsignar) {
                    this.form.controls[el.key].setValue(new Date());
                    data[el.key] = new Date();
                  } else {
                    this.form.controls[el.key].setValue(condiction.valorAsignar);
                    data[el.key] = condiction.valorAsignar;
                  }
                }
              }
              break;
            case 'Asignar valor maestra':
              if ( (starForm && (this.form.controls[el.key].value === null ||
                this.form.controls[el.key].value === '')) || !starForm) {
                if (!firstAssignMasterValue) {
                  if (!assignMasterValue) {
                    assignMasterValue = comply;
                  } else {
                    break;
                  }
                } else {
                  firstAssignMasterValue = false;
                  assignMasterValue = comply;
                }
                if (firtTimeCondition) {
                  conditonValid = assignMasterValue;
                  firtTimeCondition = false;
                }
  
                if (condiction.operadorCondicion === 'Y') {
                  if (conditonValid && assignMasterValue) {
                    assignMasterValue = false;
                  } else if ( conditonValid && !assignMasterValue) {
                    conditonValid = false;
                  }
                } else {
                  if ( !firtTimeCondition && conditonValid && assignMasterValue) {
                    assignMasterValue = true;
                  } else if (!firtTimeCondition && conditonValid && !assignMasterValue) {
                    conditonValid = false;
                  }
                  firtTimeCondition = true;
                }
                if (assignMasterValue && conditonValid) {
                  masterFilters = [];
                  let column = [`${condiction.columnaMaestra} `];
                  if (condiction.maestra !== null) {
                    if (condiction.columnaCondicionar) {
                      column.push(`${condiction.columnaCondicionar} `);
                    }
                    if (condiction.valorReferencia) {
                      column.push(`${condiction.valorReferencia} `);
                    }
  
                    specificEstructure = {
                      maestra: String(condiction.maestra),
                      columnas: column,
                      // columnas: ['244 ', '243 ', '245 ', '246 '],
                      dependencia: []
                    };
                    let logicOperator: any = 'Y';
                    if (condiction.valorReferencia === null) {
                      logicOperator = null;
                    }
                    masterFilters.push({
                      columna: condiction.columnaCondicionar,
                      valor: condiction.valor,
                      operador: '=',
                      condicional: logicOperator
                    });
                    if (condiction.valorReferencia !== null) {
                      masterFilters.push({
                        columna: condiction.valorReferencia,
                        valor: `${new Date().getFullYear()}`,
                        operador: '='
                      });
                    }
                    const valores = await this.dfds.getColumnsSpecificMasterTable(
                      specificEstructure,
                      masterFilters
                    );
                    if (!condiction.operarResultado) {
                    this.form.controls[el.key].setValue(valores[0].valores[`${condiction.columnaMaestra}`]);
                    }
                  }
                }
              }
            break;
            case 'Validar expresion':
              if (condiction.expresion && comply) {
                let new_validator = Static.copyObjet(el.validations.validators);
                new_validator.push(Validators.pattern(condiction.expresion));
                if(condiction.mensajeExpresion && this.validationMessages[el.key]["pattern"] != condiction.mensajeExpresion){
                  this.validationMessages[el.key]["pattern"] = condiction.mensajeExpresion;
                }
                this.form.controls[el.key].clearValidators();
                this.form.controls[el.key].setValidators(new_validator);
                valExpresion = true;
              }
              valView = comply;
              operactorNext = condiction.operadorCondicion;
              // this.form.controls[el.key].setValidators(el.validations.validators);
              break;
            case 'Limpiar expresion':
              if(comply){
                this.form.controls[el.key].clearValidators();
                this.form.controls[el.key].setValidators(el.validations.validators);
                valView = comply;
                valExpresion = true;
                if(this.validationMessages[el.key]["pattern"] != "Caracteres incorrectos."){
                  this.validationMessages[el.key]["pattern"] = "Caracteres incorrectos.";
                }
              }
              operactorNext = condiction.operadorCondicion;
              break;
            default:
              if (!firstBlock) {
                if (!operactorNext || operactorNext === 'O') {
                  if (valBlock || comply) {
                    valBlock = true;
                  } else {
                    valBlock = false;
                  }
                } else {
                  if (valBlock && comply) {
                    valBlock = true;
                  } else {
                    valBlock = false;
                  }
                }
                operactorNext = condiction.operadorCondicion;
              } else {
                firstBlock = false;
                firstUnlock = true;
                firstView = true;
                firstHide = true;
                firstReading = true;
                firstWriting = true;
                firstAssignValue = true;
                valBlock = comply;
                operactorNext = condiction.operadorCondicion;
              }
              if (!this.disabledForm && el.controlType !== 'label') {
                if (valBlock) {
                  this.form.controls[el.key].disable();
                  if (index > 0 || el.condictions.length === 1) {
                    if (el['default']) {
                      this.form.controls[el.key].setValue(el['default']);
                    } else {
                      this.form.controls[el.key].reset();
                    }
                  }
                } else {
                  this.form.controls[el.key].enable();
                }
              }
              break;
          }
        });
      }
      if(this.modification && !el.readonly && !el.modifiable){
        el.readonly = true;
      }else if(this.modification && !el.readonly && el.modifiable){
        el.readonly = false;
      }
      if(this.modification && el.modifiable && el.disabled && !this.addInTableModification){
        this.form.controls[el.key].enable();
      }
      if(this.modification && this.addInTableModification && !el.readonly){
        this.form.controls[el.key].enable();
      }
    });
    this.updateCondictions = false;
  }

  /**
   * Se setea la propiedad disable en true si la propiedad del formulario disabledForm esta en true
   * Nota: este se utiliza para los controles de un contenedor
   *
   * @private
   * @param {BaseControl<any>[]} controls
   * @returns {BaseControl<any>[]}
   * @memberof DynamicFormComponent
   */
  private formatDisabledForm(controls: BaseControl<any>[]): BaseControl<any>[] {
    controls.forEach(el => {
      if (this.disabledForm) {
        if (el.controlType === 'container') {
          el['controls'] = this.formatDisabledForm(el['controls']);
        } else {
          if (!el.skipDisabled) {
            el.disabled = true;
          }
        }
      }
    }
    );
    return controls;
  }

  /**
   * Revisa las reglas de validación de un campo y establece
   * los mensajes de validación correspondientes.
   *
   * @private
   * @param {*} [items]
   * @returns
   *
   * @memberOf DynamicFormComponent
   */
  private configureMessagesValidation(items?: BaseControl<any>[]) {
    // Generar object para gestionar los errores de validación.
    // Ie: { nombreKeyControl(id): '' }
    const controls = items ? items : this.controls;
    controls.forEach(el => {
      if (el.controlType === 'container') {
        this.configureMessagesValidation((<Container>el).controls);
      } else {
        if (!isEmpty(el.key)) {
          this.formErrors[el.key] = [];
        }
      }
    });
    this.validateControls();
  }

  /**
   * Recorre cada control del formulario y verifica que
   * las reglas de validación se esten cumpliendo.
   *
   * @param {boolean} [ignoreDirty=false] - Realiza la validación ignorando si el campo esta sucio.
   * @memberof DynamicFormComponent
   */
  validateControls(ignoreDirty: boolean = false) {
    const form = this.form;
    // Recorre cada campo verificando que el campo sea valido.
    // En caso de que no sea valido se generan sus mensajes de error.
    Object.keys(this.formErrors).forEach(field => {
      // Limpiar errores previos si existen
      this.formErrors[field] = [];
      const control = form.get(field);
      if (control && control.invalid) {
        if (ignoreDirty || control.dirty || indexOf(this.controlDirty, field) !== -1) {
          const messages = this.validationMessages[field];
          Object.keys(control.errors).forEach(key => {
            this.formErrors[field].push(messages[key]);
          });
        }
      }
    });
  }

  /**
   * Controla los eventos de accion del seletor en modal
   *
   * @param {AccionModal} data
   * @memberof DynamicFormComponent
   */
  actionSelectModal(data: AccionModal) {
    this.actionSelector.emit(data);
  }

  /**
   * Retorna la configuracion de un control del formulario
   *
   * @private
   * @param {string} key
   * @param {BaseControl<any>[]} [controlsContainer]
   * @returns {BaseControl<any>}
   * @memberof DynamicFormComponent
   */
  private getConfigControl(key: string, controlsContainer?: BaseControl<any>[]): BaseControl<any> {
    let control: BaseControl<any>;
    let controls: BaseControl<any>[] = [];
    if (controlsContainer && controlsContainer.length) {
      controls = controlsContainer;
    } else {
      controls = this.controls;
    }
    control = find(controls, {key: key});
    if (!control) {
      if (controls.length) {
        for (const ctrl of controls) {
          if (ctrl.controlType === 'container') {
            control = this.getConfigControl(key, ctrl['controls']);
            if (control) {
              break;
            }
          }
        }
      }
    }
    return control;
  }

  /**
   * Ejecuta una condicion en especifico de un campo
   *
   * @private
   * @param {BaseControl<any>[]} allElements
   * @param {CondicionControl} condiction
   * @returns {boolean}
   * @memberof DynamicFormComponent
   */
  private executeCondiction (
    allElements: BaseControl<any>[],
    condiction: CondicionControl
  ): boolean {
    let date = false;
    let comply = false;
    let value: any;
    const moment = require('moment');
    if (!Number(condiction.valor)) {
      if (condiction.valor && moment(new Date(String(condiction.valor))).isValid()) {
        date = true;
      } else if (condiction.fechaActual) {
        condiction.valor = new Date();
        date = true;
      } else {
        date = false;
      }
    } else {
      date = false;
    }
    const field = find(allElements, {key: condiction.campoDepende});
    if (!date) {
      if (this.form.controls[condiction.campoDepende]) {
        if (field && field['type'] === 'number' && field['thousandsSeparator']) {
          value = Static.numberFormatWithoutThousandsSeparator(
            this.form.controls[condiction.campoDepende].value,
            field['thousandsSeparator'],
            field['decimalSeparator']
          ).toLowerCase();
        } else {
          value = String(this.form.controls[condiction.campoDepende].value).toLowerCase();
        }
      }
    }
    if (this.form.controls[condiction.campoDepende]) {
      switch (condiction.siEs) {
        case 'Vacio':
          if (
            !this.form.controls[condiction.campoDepende].value ||
            this.form.controls[condiction.campoDepende].value === ''
          ) {
            comply = true;
          } else {
            comply = false;
          }
          break;
        case 'Diferente':
          if (!date) {
            // tslint:disable-next-line:max-line-length
            if (value !== String(condiction.valor).toLowerCase()) {
              comply = true;
            } else {
              comply = false;
            }
          } else {
            let dtValorControl: Date = null;
            if (field && field.controlType === 'datetimepicker') {
              let valorCondiction: string = null;
              let valorControl: string = null;
              const dtValor: Date = new Date(condiction.valor);
              switch (field['pickerType']) {
                case 'calendar':
                  valorCondiction = dtValor.toLocaleDateString();
                  dtValorControl = this.form.controls[condiction.campoDepende].value;
                  if (dtValorControl) {
                    valorControl = dtValorControl.toLocaleDateString();
                  }
                  break;
                case 'timer':
                  dtValor.setSeconds(0, 0);
                  valorCondiction = dtValor.toLocaleTimeString();
                  dtValorControl = this.form.controls[condiction.campoDepende].value;
                  if (dtValorControl) {
                    dtValorControl.setSeconds(0, 0);
                    valorControl = dtValorControl.toLocaleTimeString();
                  }
                  break;
                default:
                  dtValor.setSeconds(0, 0);
                  dtValorControl = this.form.controls[condiction.campoDepende].value;
                  valorCondiction = dtValor.toLocaleDateString();
                  valorCondiction += ' ' + dtValor.toLocaleTimeString();
                  if (dtValorControl) {
                    dtValorControl.setSeconds(0, 0);
                    valorControl = dtValorControl.toLocaleDateString();
                    valorControl += ' ' + dtValorControl.toLocaleTimeString();
                  }
                  break;
              }
              if (valorControl !== valorCondiction) {
                comply = true;
              } else {
                comply = false;
              }
            } else {
              dtValorControl = this.form.controls[condiction.campoDepende].value;
              // tslint:disable-next-line:max-line-length
              if (dtValorControl instanceof Date && dtValorControl.toLocaleDateString() !== new Date(condiction.valor).toLocaleDateString()) {
                comply = true;
              } else {
                comply = false;
              }
            }
          }
          break;
        case 'Mayor a':
          if (!date) {
            if ((value !== '' && value !== undefined && value !== null) && Number(value) > Number(condiction.valor)) {
              comply = true;
            } else {
              comply = false;
            }
          } else {
            let dtValorControl: Date = null;
            let valorCondiction: Date = null;
            valorCondiction = new Date(condiction.valor);
            dtValorControl = this.form.controls[condiction.campoDepende].value;
            if (field && field.controlType === 'datetimepicker') {
              switch (field['pickerType']) {
                case 'calendar':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                      dtValorControl.setHours(0, 0, 0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setHours(0, 0, 0, 0);
                  }
                  break;
                case 'timer':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setDate(new Date().getDate());
                    dtValorControl.setMonth(new Date().getMonth());
                    dtValorControl.setFullYear(new Date().getFullYear());
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setDate(new Date().getDate());
                    valorCondiction.setMonth(new Date().getMonth());
                    valorCondiction.setFullYear(new Date().getFullYear());
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
                default:
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
              }
            } else {
              if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                  dtValorControl.setHours(0, 0, 0, 0);
              }
              if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                valorCondiction.setHours(0, 0, 0, 0);
              }
            }
            // Operacion
            if (
              (dtValorControl instanceof Date && moment(dtValorControl).isValid()) &&
              (valorCondiction instanceof Date && moment(valorCondiction).isValid()) &&
              dtValorControl > valorCondiction
            ) {
              comply = true;
            } else {
              comply = false;
            }
          }
          break;
        case 'Mayor o igual a':
          if (!date) {
            if ((value !== '' && value !== undefined && value !== null) && Number(value) >= Number(condiction.valor)) {
              comply = true;
            } else {
              comply = false;
            }
          } else {
            let dtValorControl: Date = null;
            let valorCondiction: Date = null;
            valorCondiction = new Date(condiction.valor);
            dtValorControl = this.form.controls[condiction.campoDepende].value;
            if (field && field.controlType === 'datetimepicker') {
              switch (field['pickerType']) {
                case 'calendar':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                      dtValorControl.setHours(0, 0, 0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setHours(0, 0, 0, 0);
                  }
                  break;
                case 'timer':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid() ) {
                    dtValorControl.setDate(new Date().getDate());
                    dtValorControl.setMonth(new Date().getMonth());
                    dtValorControl.setFullYear(new Date().getFullYear());
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid() ) {
                    valorCondiction.setDate(new Date().getDate());
                    valorCondiction.setMonth(new Date().getMonth());
                    valorCondiction.setFullYear(new Date().getFullYear());
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
                default:
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
              }
            } else {
              if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                  dtValorControl.setHours(0, 0, 0, 0);
              }
              if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                valorCondiction.setHours(0, 0, 0, 0);
              }
            }
            // Operacion
            if (
              (dtValorControl instanceof Date && moment(dtValorControl).isValid()) &&
              (valorCondiction instanceof Date && moment(valorCondiction).isValid()) &&
              dtValorControl >= valorCondiction
            ) {
              comply = true;
            } else {
              comply = false;
            }
          }
          break;
        case 'Menor a':
          if (!date) {
            if ((value !== '' && value !== undefined && value !== null) && Number(value) < Number(condiction.valor)) {
              comply = true;
            } else {
              comply = false;
            }
          } else {
            let dtValorControl: Date = null;
            let valorCondiction: Date = null;
            valorCondiction = new Date(condiction.valor);
            dtValorControl = this.form.controls[condiction.campoDepende].value;
            if (field && field.controlType === 'datetimepicker') {
              switch (field['pickerType']) {
                case 'calendar':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                      dtValorControl.setHours(0, 0, 0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setHours(0, 0, 0, 0);
                  }
                  break;
                case 'timer':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setDate(new Date().getDate());
                    dtValorControl.setMonth(new Date().getMonth());
                    dtValorControl.setFullYear(new Date().getFullYear());
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setDate(new Date().getDate());
                    valorCondiction.setMonth(new Date().getMonth());
                    valorCondiction.setFullYear(new Date().getFullYear());
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
                default:
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
              }
            } else {
              if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                  dtValorControl.setHours(0, 0, 0, 0);
              }
              if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                valorCondiction.setHours(0, 0, 0, 0);
              }
            }
            // Operacion
            if (
              (dtValorControl instanceof Date && moment(dtValorControl).isValid()) &&
              (valorCondiction instanceof Date && moment(valorCondiction).isValid()) &&
              dtValorControl < valorCondiction
            ) {
              comply = true;
            } else {
              comply = false;
            }
          }
          break;
        case 'Menor o igual a':
          if (!date) {
            if ((value !== '' && value !== undefined && value !== null) && Number(value) <= Number(condiction.valor)) {
              comply = true;
            } else {
              comply = false;
            }
          } else {
            let dtValorControl: Date = null;
            let valorCondiction: Date = null;
            valorCondiction = new Date(condiction.valor);
            dtValorControl = this.form.controls[condiction.campoDepende].value;
            if (field && field.controlType === 'datetimepicker') {
              switch (field['pickerType']) {
                case 'calendar':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                      dtValorControl.setHours(0, 0, 0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setHours(0, 0, 0, 0);
                  }
                  break;
                case 'timer':
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setDate(new Date().getDate());
                    dtValorControl.setMonth(new Date().getMonth());
                    dtValorControl.setFullYear(new Date().getFullYear());
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setDate(new Date().getDate());
                    valorCondiction.setMonth(new Date().getMonth());
                    valorCondiction.setFullYear(new Date().getFullYear());
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
                default:
                  if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                    dtValorControl.setSeconds(0, 0);
                  }
                  if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                    valorCondiction.setSeconds(0, 0);
                  }
                  break;
              }
            } else {
              if (dtValorControl && dtValorControl instanceof Date && moment(dtValorControl).isValid()) {
                  dtValorControl.setHours(0, 0, 0, 0);
              }
              if (valorCondiction && valorCondiction instanceof Date && moment(valorCondiction).isValid()) {
                valorCondiction.setHours(0, 0, 0, 0);
              }
            }
            // Operacion
            if (
              (dtValorControl instanceof Date && moment(dtValorControl).isValid()) &&
              (valorCondiction instanceof Date && moment(valorCondiction).isValid()) &&
              dtValorControl <= valorCondiction
            ) {
              comply = true;
            } else {
              comply = false;
            }
          }
          break;
        default:
          if (!date) {
            // tslint:disable-next-line:max-line-length
            if (value === String(condiction.valor).toLowerCase()) {
              comply = true;
            } else {
              comply = false;
            }
          } else {
            let dtValorControl: Date = null;
            if (field && field.controlType === 'datetimepicker') {
              let valorCondiction: string = null;
              let valorControl: string = null;
              const time: Date = new Date(condiction.valor);
              switch (field['pickerType']) {
                case 'calendar':
                  valorCondiction = new Date(condiction.valor).toLocaleDateString();
                  dtValorControl = this.form.controls[condiction.campoDepende].value;
                  if (dtValorControl) {
                    valorControl = dtValorControl.toLocaleDateString();
                  }
                  break;
                case 'timer':
                  time.setSeconds(0, 0);
                  valorCondiction = time.toLocaleTimeString();
                  dtValorControl = this.form.controls[condiction.campoDepende].value;
                  if (dtValorControl) {
                    dtValorControl.setSeconds(0, 0);
                    valorControl = dtValorControl.toLocaleTimeString();
                  }
                  break;
                default:
                  time.setSeconds(0, 0);
                  dtValorControl = this.form.controls[condiction.campoDepende].value;
                  valorCondiction = time.toLocaleDateString();
                  valorCondiction += ' ' + time.toLocaleTimeString();
                  if (dtValorControl) {
                    dtValorControl.setSeconds(0, 0);
                    valorControl = dtValorControl.toLocaleDateString();
                    valorControl += ' ' + dtValorControl.toLocaleTimeString();
                  }
                  break;
              }
              if (valorControl === valorCondiction) {
                comply = true;
              } else {
                comply = false;
              }
            } else {
              dtValorControl = this.form.controls[condiction.campoDepende].value;
              // tslint:disable-next-line:max-line-length
              if (dtValorControl instanceof Date && dtValorControl.toLocaleDateString() === new Date(condiction.valor).toLocaleDateString()) {
                comply = true;
              } else {
                comply = false;
              }
            }
          }
          break;
      }
    }
    return comply;
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/shared/dynamic-form/dynamic-form.component.ts